import { useMemo } from "react";
import dayjs from "dayjs";
import { useEnum } from "@/api/services/global/enum";
import { useGetNationalityList } from "@/api/services/global/master";
import { useGetListCompanyByRso } from "@/api/services/main/company";
import { useLearningProgressExamList } from "@/api/services/main/learning-progress";
import { TExamListItemDto } from "@/api/services/main/learning-progress/dtos/exam/get-exam-list-by-industry.dto";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { EXAM_STATUS, USER_TYPE } from "@/types/enum";
import { TOption } from "@/types/common";

// Past exam = explicit "ended" status OR examMonth in the past (defensive: BE status value may vary).
const isPastExam = (exam: TExamListItemDto): boolean => {
  if (exam.status === EXAM_STATUS.ENDED) return true;
  if (exam.examMonth) {
    const month = dayjs(exam.examMonth);
    if (month.isValid() && month.endOf("month").isBefore(dayjs())) return true;
  }
  return false;
};

export const useLearningProgressFilterMasterData = (industryId?: number) => {
  const { userType } = useInitSetup();
  // Only RSO users can filter by 所属機関 — gate both the field and the company API call on this.
  const canFilterByOrganization = userType === USER_TYPE.RSO_USER;

  const { nationalityOptions } = useGetNationalityList();
  const { data: companyData } = useGetListCompanyByRso(canFilterByOrganization);
  const { data: japaneseLevelOptions } = useEnum("EJapaneseLevelType");
  const { data: examListData } = useLearningProgressExamList({ industryId: industryId ?? 0 });

  const companyOptions = useMemo<TOption[]>(() => {
    const companies = companyData?.data ?? [];
    return companies.filter((c) => !c.isArchive).map((c) => ({ value: String(c.id), label: c.companyName }));
  }, [companyData]);

  const nationalityStringOptions = useMemo<TOption[]>(
    () => nationalityOptions.map((opt) => ({ ...opt, value: String(opt.value) })),
    [nationalityOptions],
  );

  // 試験予定: use exam list for the current industry. value = exam id, label = periodName.
  // 過去の試験はリストの末尾に並べ替える。グループ内は元の順序を保持。
  const examScheduleOptions = useMemo<TOption[]>(() => {
    const exams = examListData?.data ?? [];
    const upcoming = exams.filter((e) => !isPastExam(e));
    const past = exams.filter((e) => isPastExam(e));
    return [...upcoming, ...past].map((exam) => ({
      value: String(exam.id),
      label: exam.periodName,
    }));
  }, [examListData]);

  // Progress buckets used by 学習進捗. value encoding: "min-max" → server side processes as min ≦ x < max
  // (the last bucket includes 100%).
  const progressOptions: TOption[] = useMemo(
    () => [
      { value: "0-19", label: "0〜19%" },
      { value: "20-39", label: "20〜39%" },
      { value: "40-59", label: "40〜59%" },
      { value: "60-79", label: "60〜79%" },
      { value: "80-100", label: "80〜100%" },
    ],
    [],
  );

  return {
    canFilterByOrganization,
    options: {
      organization: companyOptions,
      nationality: nationalityStringOptions,
      japaneseLevel: (japaneseLevelOptions ?? []) as TOption[],
      examSchedule: examScheduleOptions,
      progress: progressOptions,
    },
  };
};
